import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

import {
  ConfigProvider,
  Input,
  InputNumber,
  Select,
  SelectProps,
  Spin,
} from 'antd';
import type { GetRef } from 'antd';
import DateFieldEditor from 'components/common/DateFieldEditor';
import VoiceMicButton from 'components/common/VoiceMicButton/VoiceMicButton';
import {
  VALUE_AT_LEAST_MESSAGE,
  VALUE_GREATER_THAN_MESSAGE,
  VALUE_REQUIRED_MESSAGE,
} from 'constants/messageConstants';
import { useSpeechInput } from 'hooks/useSpeechInput';
import MergeClassNames from 'utils/MergeClassNames';

export interface EditableSelectOption {
  value: string;
  label: string;
}

// Backs a remote (server-searched, paginated) select cell, e.g. a G/M
// catalogue merge — as opposed to the default client-side filter over an
// already fully-loaded `options` list.
export interface EditableSelectRemoteSearch {
  searchValue: string;
  onSearch: (value: string) => void;
  loading?: boolean;
  isPaginating?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  /** Shown in place of "No data" when nothing matches, e.g. an "Add" row (OP-2498). */
  emptyAction?: React.ReactNode;
}

interface EditableCellSharedProps {
  className?: string;
  disabled?: boolean;
  /** Left out of the Tab order; still opens on click (e.g. a grid's Notes). */
  skipTabStop?: boolean;
}

interface EditableNumberCellProps extends EditableCellSharedProps {
  type: 'number';
  value: number | null;
  displayValue: React.ReactNode;
  min?: number;
  inclusiveMin?: boolean;
  allowEmpty?: boolean;
  /** Handles Arrow Up/Down while editing (e.g. move to the adjacent row); value stepping is always disabled. */
  onArrowNavigate?: (direction: 'up' | 'down', from: HTMLElement) => void;
  onCommit: (value: number | null) => void;
}

interface EditableSelectCellProps extends EditableCellSharedProps {
  type: 'select';
  value: string | null;
  displayValue: React.ReactNode;
  options: EditableSelectOption[];
  placeholder?: string;
  /** Clear also empties the value; by default it clears search text only. */
  allowClear?: boolean;
  remoteSearch?: EditableSelectRemoteSearch;
  /** false lets the option list grow wider than the cell, so long options don't truncate. */
  popupMatchSelectWidth?: boolean;
  onEditStart?: () => void;
  onCommit: (value: string | null) => void;
}

interface EditableDateCellProps extends EditableCellSharedProps {
  type: 'date';
  value: string | null;
  hasDay: boolean;
  displayValue: React.ReactNode;
  fallbackDate?: string | null;
  onCommit: (value: string | null, hasDay: boolean) => void;
  /** Fires once the picker closes, whether a date was committed or not. */
  onEditEnd?: () => void;
}

interface EditableTextCellProps extends EditableCellSharedProps {
  type: 'text';
  value: string | null;
  displayValue: React.ReactNode;
  placeholder?: string;
  maxLength?: number;
  /**
   * Always show the textarea without requiring a click to enter edit mode.
   * Intended for form contexts (e.g. report modals) where the field should
   * always be editable. Default: false (click-to-edit).
   */
  alwaysEdit?: boolean;
  /**
   * Fires on every keystroke in alwaysEdit mode. Use when the parent needs
   * live updates (e.g. a character counter driven by parent state, or a
   * print preview). Ignored in click-to-edit mode.
   */
  onChange?: (value: string) => void;
  /** Fixed row count for the textarea in alwaysEdit mode. Omit to use autoSize. */
  rows?: number;
  /**
   * Text appended after the count, e.g. ' characters' → '42/250 characters'.
   * Only applies when maxLength is set. Default: '' (no suffix).
   */
  counterSuffix?: string;
  /**
   * Where the character counter appears relative to the textarea.
   * 'right' (default) mirrors AntD showCount; 'left' aligns it to the start.
   */
  counterPosition?: 'left' | 'right';
  /**
   * Override className for the counter text element. When omitted a default
   * grey style is used; turns danger-coloured when softLimit + over-limit.
   */
  counterClassName?: string;
  /**
   * false (default): input is hard-blocked at maxLength (AntD enforces it).
   * true: user can type past maxLength but the counter turns danger-coloured.
   */
  softLimit?: boolean;
  onCommit: (value: string | null) => void;
}

// Multi-value select. Values are plain strings (catalogue globalIds once a
// remote source is wired up, or free-typed tags in the meantime via
// `tagMode`) rather than IItem objects — callers own the id/label mapping.
interface EditableMultiSelectCellProps extends EditableCellSharedProps {
  type: 'multiselect';
  value: string[];
  displayValue: React.ReactNode;
  options: EditableSelectOption[];
  placeholder?: string;
  tagMode?: boolean;
  remoteSearch?: EditableSelectRemoteSearch;
  onEditStart?: () => void;
  onCommit: (value: string[]) => void;
}

type EditableCellProps =
  | EditableNumberCellProps
  | EditableSelectCellProps
  | EditableDateCellProps
  | EditableTextCellProps
  | EditableMultiSelectCellProps;

const buildRemoteSearchSelectProps = (
  remoteSearch: EditableSelectRemoteSearch | undefined
): Partial<SelectProps> =>
  remoteSearch
    ? {
        filterOption: false,
        searchValue: remoteSearch.searchValue,
        onSearch: remoteSearch.onSearch,
        loading: remoteSearch.loading,
        notFoundContent: remoteSearch.loading ? (
          <div className="flex items-center justify-center py-2">
            <Spin size="small" />
          </div>
        ) : (
          remoteSearch.emptyAction
        ),
        onPopupScroll: (event) => {
          const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
          if (
            scrollTop + clientHeight >= scrollHeight - 20 &&
            remoteSearch.hasNextPage
          ) {
            remoteSearch.onLoadMore?.();
          }
        },
        popupRender: (menu) => (
          <>
            {menu}
            {remoteSearch.isPaginating && (
              <div className="flex justify-center pb-2 pt-1">
                <Spin size="small" />
              </div>
            )}
          </>
        ),
      }
    : { optionFilterProp: 'label' };

/** Marks the focusable root of a number cell so callers can target it without relying on tabIndex. */
export const EDITABLE_NUMBER_CELL_ATTR = 'data-editable-number-cell';

const validateNumber = (
  value: number | null,
  min?: number,
  inclusiveMin?: boolean,
  allowEmpty?: boolean
): string | null => {
  if (value === null) return allowEmpty ? null : VALUE_REQUIRED_MESSAGE;
  if (min !== undefined) {
    const isBelowMin = inclusiveMin ? value < min : value <= min;
    if (isBelowMin) {
      return inclusiveMin
        ? VALUE_AT_LEAST_MESSAGE(min)
        : VALUE_GREATER_THAN_MESSAGE(min);
    }
  }
  return null;
};

/**
 * rc-select treats Tab like Enter (picks the highlighted option, blocks focus
 * moving). Stopping it here lets Tab/Shift+Tab leave the cell value unchanged.
 *
 * Only stopPropagation — not preventDefault — is called: stopPropagation keeps
 * rc-select's bubble-phase listener from seeing the event, while leaving the
 * browser free to move focus (preventDefault would cancel that native action).
 */
const stopTabReachingSelect = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (event.key === 'Tab') event.stopPropagation();
};

/**
 * rc-select closes a single select on mousedown of its clear button, unmounting
 * it before the click that clears. Stopping it lets that click land.
 */
const keepOpenOnClearMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
  if (!(event.target as HTMLElement).closest('.ant-select-clear')) return;
  // Mirrors rc-select's own handler: keep focus in the search input.
  event.preventDefault();
  event.stopPropagation();
};

/**
 * rc-select treats focus moving between its input and clear button as a blur,
 * which locks the option list so it stops re-filtering. Focus that stays in
 * the select is not a blur, so keep it from reaching rc-select. The wrapper
 * itself is excluded so a click on its border still closes the editor.
 */
const ignoreBlurWithinSelect = (event: React.FocusEvent<HTMLDivElement>) => {
  const selectRoot = event.currentTarget.querySelector('.ant-select');
  if (selectRoot?.contains(event.relatedTarget as Node | null)) {
    event.stopPropagation();
  }
};

const EditableCell: React.FC<EditableCellProps> = (props) => {
  const { type, displayValue, className, disabled, skipTabStop } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState<number | null>(null);
  const [draftText, setDraftText] = useState<string | null>(null);
  const [draftMultiValues, setDraftMultiValues] = useState<string[]>([]);
  // Read on close: a Tab-out in tags mode adds the tag and closes in one event.
  const draftMultiValuesRef = useRef<string[]>([]);
  const updateDraftMultiValues = useCallback((values: string[]) => {
    draftMultiValuesRef.current = values;
    setDraftMultiValues(values);
  }, []);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<GetRef<typeof InputNumber>>(null);
  const textInputRef = useRef<GetRef<typeof Input.TextArea>>(null);
  // Synced each render so the effect always reads the current callback without
  // taking a dep on props.onEditStart (callers often pass arrow functions).
  const onEditStartRef = useRef<(() => void) | undefined>(undefined);
  onEditStartRef.current =
    props.type === 'select' || props.type === 'multiselect'
      ? props.onEditStart
      : undefined;

  // Voice input — state refs are updated synchronously during text-type renders
  // so the onTranscript callback always reads the correct, fresh values without
  // needing to be recreated whenever draftText changes.
  const voiceStateRef = useRef<{
    text: string;
    maxLength: number | undefined;
    softLimit: boolean;
    onChange: ((v: string) => void) | undefined;
  }>({ text: '', maxLength: undefined, softLimit: false, onChange: undefined });

  const speechInput = useSpeechInput({
    onTranscript: useCallback((transcript: string) => {
      const { text, maxLength, softLimit, onChange } = voiceStateRef.current;
      const textarea = textInputRef.current?.resizableTextArea?.textArea;
      const selStart = textarea?.selectionStart ?? text.length;
      const selEnd = textarea?.selectionEnd ?? text.length;

      let toInsert = transcript;
      if (!softLimit && maxLength !== undefined) {
        const available = maxLength - text.length + (selEnd - selStart);
        if (available <= 0) return;
        toInsert = transcript.slice(0, available);
      }

      const newText = text.slice(0, selStart) + toInsert + text.slice(selEnd);
      setDraftText(newText);
      onChange?.(newText);

      // Restore focus and cursor after React re-renders the textarea value.
      const newCursorPos = selStart + toInsert.length;
      requestAnimationFrame(() => {
        textarea?.focus();
        textarea?.setSelectionRange(newCursorPos, newCursorPos);
      });
    }, []), // stable: only reads refs and calls stable setDraftText
  });

  useEffect(() => {
    if (isEditing && type === 'number') {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    if (isEditing && type === 'text') {
      textInputRef.current?.focus({ cursor: 'end' });
    }
  }, [isEditing, type]);

  useEffect(() => {
    if (isEditing) onEditStartRef.current?.();
  }, [isEditing]);

  const enterEdit = () => {
    if (disabled || isEditing) return;
    setError(null);
    setDraftValue(props.type === 'number' ? props.value : null);
    setDraftText(props.type === 'text' ? props.value : null);
    updateDraftMultiValues(props.type === 'multiselect' ? props.value : []);
    setSearchText('');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setError(null);
    setIsEditing(false);
  };

  /** Single source of truth for number-draft validation (commit and arrow navigation). */
  const getNumberDraftError = (): string | null =>
    props.type === 'number'
      ? validateNumber(
          draftValue,
          props.min,
          props.inclusiveMin,
          props.allowEmpty
        )
      : null;

  const commitNumber = () => {
    if (props.type !== 'number') return;
    const validationError = getNumberDraftError();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setIsEditing(false);
    if (draftValue === props.value) return;
    props.onCommit(draftValue);
  };

  const hasSelectSearchText =
    props.type === 'select' &&
    (props.remoteSearch?.searchValue ?? searchText) !== '';

  const commitSelect = (value: string | undefined) => {
    if (props.type !== 'select') return;
    // Clear only wipes search text, unless allowClear and none is left.
    if (value === undefined && (!props.allowClear || hasSelectSearchText))
      return;
    setIsEditing(false);
    const nextValue = value ?? null;
    if (nextValue === props.value) return;
    props.onCommit(nextValue);
  };

  const commitDate = (value: string | null, hasDay: boolean) => {
    if (props.type !== 'date') return;
    setIsEditing(false);
    if (value === props.value && hasDay === props.hasDay) return;
    props.onCommit(value, hasDay);
  };

  const commitText = () => {
    if (props.type !== 'text') return;
    setIsEditing(false);
    const trimmed = draftText?.trim();
    const nextValue = trimmed ? draftText : null;
    if (nextValue === props.value) return;
    props.onCommit(nextValue);
  };

  const commitMultiSelect = (values: string[]) => {
    if (props.type !== 'multiselect') return;
    setIsEditing(false);
    props.onCommit(values);
  };

  const rootClassName = MergeClassNames(
    // border (1px), not border-2 — thinned from 2px per feedback that the
    // hover line read as too thick. This is the *reserved* width, used
    // unconditionally at rest/hover/editing alike (never toggled between
    // states): every other "reserved space" div across this grid
    // (Required, Rate/Rate-unit/Cost blanks, $/ha) matches it for pixel
    // alignment, and keeping it constant is what avoids a *width* change on
    // hover in the first place (see the hover comment below).
    'relative flex items-center border border-transparent',
    !disabled &&
      (type === 'select' || type === 'multiselect'
        ? 'cursor-pointer'
        : 'cursor-text'),
    // Field-level hover recolors the cell's own existing border (the border
    // above) rather than drawing a separate ring shape — color only, never
    // width. These fields aren't pinned to a fixed height (the
    // task-row-pill fields size via flex `items-stretch` with no explicit
    // height; a plain in-flow cell sizes to its own content), so a
    // *border-width* change on hover shrinks/grows the field's own intrinsic
    // height and made the whole row visibly resize on hover ("twitch") —
    // tried once and reverted. Color is the only property that can change
    // here without affecting box size. Row-level hover was tried (both a
    // fill and an outline) and dropped, since outline/border on an antd <tr>
    // doesn't reliably render as a continuous rectangle in real browsers.
    !isEditing && !disabled && 'hover:z-10 hover:border-success',
    isEditing && 'z-10 bg-white',
    isEditing && !error && 'border-success-dark',
    isEditing && error && 'border-danger',
    className
  );

  if (props.type === 'select') {
    const searchSelectProps = buildRemoteSearchSelectProps(props.remoteSearch);

    return (
      <div
        className={rootClassName}
        tabIndex={disabled || isEditing || skipTabStop ? -1 : 0}
        onFocus={() => !isEditing && enterEdit()}
        onClick={(event) => {
          event.stopPropagation();
          enterEdit();
        }}
        onKeyDownCapture={stopTabReachingSelect}
        onMouseDownCapture={keepOpenOnClearMouseDown}
        onBlurCapture={ignoreBlurWithinSelect}
      >
        {isEditing ? (
          <Select
            autoFocus
            open
            variant="borderless"
            className={MergeClassNames(
              'editable-cell-select h-full w-full',
              !props.allowClear && 'editable-cell-select-search-clear'
            )}
            placeholder={props.placeholder}
            value={props.value ?? undefined}
            options={props.options}
            // A value with no matching option (e.g. a unit outside the
            // step's list, or options still loading) would otherwise show
            // its raw id; show the cell's own label instead (OP-2464).
            labelRender={({ label }) => label ?? displayValue}
            allowClear={hasSelectSearchText || props.allowClear}
            showSearch
            popupMatchSelectWidth={props.popupMatchSelectWidth ?? true}
            suffixIcon={<IoMdArrowDropdown className="text-gray-dark" />}
            onChange={commitSelect}
            // Local search state — defaults for non-remote search. When
            // remoteSearch is set, searchSelectProps spreads its own
            // searchValue/onSearch below and silently overrides these.
            searchValue={searchText}
            onSearch={setSearchText}
            onOpenChange={(open) => {
              if (!open) cancelEdit();
            }}
            {...searchSelectProps}
          />
        ) : (
          <div className="flex h-full w-full items-center px-2 py-1">
            <span className="block w-full flex-1 truncate">{displayValue}</span>
            <IoMdArrowDropdown className="ml-1 flex-shrink-0 text-gray-dark" />
          </div>
        )}
      </div>
    );
  }

  if (props.type === 'multiselect') {
    const searchSelectProps = buildRemoteSearchSelectProps(props.remoteSearch);

    return (
      <div
        className={rootClassName}
        tabIndex={disabled || isEditing || skipTabStop ? -1 : 0}
        onFocus={() => !isEditing && enterEdit()}
        onClick={(event) => {
          event.stopPropagation();
          enterEdit();
        }}
        onKeyDownCapture={stopTabReachingSelect}
      >
        {isEditing ? (
          <Select
            autoFocus
            open
            mode={props.tagMode ? 'tags' : 'multiple'}
            variant="borderless"
            className="editable-cell-select h-full w-full"
            placeholder={props.placeholder}
            value={draftMultiValues}
            options={props.options}
            showSearch
            suffixIcon={<IoMdArrowDropdown className="text-gray-dark" />}
            onChange={updateDraftMultiValues}
            onOpenChange={(open) => {
              if (!open) commitMultiSelect(draftMultiValuesRef.current);
            }}
            {...searchSelectProps}
          />
        ) : (
          <div className="flex h-full w-full items-center px-2 py-1">
            <span className="block w-full flex-1 truncate">{displayValue}</span>
            <IoMdArrowDropdown className="ml-1 flex-shrink-0 text-gray-dark" />
          </div>
        )}
      </div>
    );
  }

  if (props.type === 'date') {
    return (
      <div
        className={rootClassName}
        tabIndex={disabled || isEditing || skipTabStop ? -1 : 0}
        onFocus={() => !isEditing && enterEdit()}
        onClick={(event) => {
          event.stopPropagation();
          enterEdit();
        }}
      >
        {isEditing ? (
          <DateFieldEditor
            className="w-full"
            value={props.value}
            hasDay={props.hasDay}
            fallbackDate={props.fallbackDate}
            onCommit={commitDate}
            onClose={() => {
              cancelEdit();
              props.onEditEnd?.();
            }}
          />
        ) : (
          <span className="block w-full truncate px-2 py-1">
            {displayValue}
          </span>
        )}
      </div>
    );
  }

  if (props.type === 'text') {
    const {
      alwaysEdit = false,
      onChange: onChangeLive,
      rows,
      counterSuffix = '',
      counterPosition = 'right',
      counterClassName,
      softLimit = false,
    } = props;

    // Keep voice state fresh for the stable onTranscript callback.
    const effectiveTextForVoice = draftText ?? props.value ?? '';
    voiceStateRef.current = {
      text: alwaysEdit ? effectiveTextForVoice : (draftText ?? ''),
      maxLength: props.maxLength,
      softLimit,
      onChange: onChangeLive,
    };

    if (alwaysEdit) {
      const effectiveText = effectiveTextForVoice;
      const charCount = effectiveText.length;
      const isOverLimit =
        props.maxLength !== undefined && charCount > props.maxLength;

      const defaultCounterCls = MergeClassNames(
        'text-xs tabular-nums text-gray-muted',
        softLimit && isOverLimit && 'text-danger'
      );

      const counterText =
        props.maxLength !== undefined
          ? `${charCount}/${props.maxLength}${counterSuffix}`
          : undefined;

      return (
        <div className={MergeClassNames('flex flex-col', className)}>
          <Input.TextArea
            ref={textInputRef}
            placeholder={props.placeholder}
            maxLength={softLimit ? undefined : props.maxLength}
            value={effectiveText}
            {...(rows ? { rows } : { autoSize: { minRows: 2, maxRows: 6 } })}
            onChange={(event) => {
              const v = event.target.value;
              setDraftText(v);
              onChangeLive?.(v);
            }}
            onBlur={commitText}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                cancelEdit();
              }
            }}
          />
          {/* Mic button always rendered (grayed when unsupported); counter
              shown alongside it when maxLength is set. */}
          <div
            className={MergeClassNames(
              'mt-1 flex items-center gap-1',
              counterPosition === 'right' ? 'justify-end' : 'justify-start'
            )}
          >
            <VoiceMicButton
              isSupported={speechInput.isSupported}
              isRecording={speechInput.isRecording}
              lang={speechInput.lang}
              langOptions={speechInput.langOptions}
              onToggle={speechInput.toggle}
              onLangChange={speechInput.setLang}
              disabled={disabled}
            />
            {counterText !== undefined && (
              <span
                className={MergeClassNames(
                  counterClassName ?? defaultCounterCls
                )}
              >
                {counterText}
              </span>
            )}
          </div>
        </div>
      );
    }

    // Click-to-edit text mode.
    // Layout mirrors alwaysEdit: the cell border wraps only the textarea, and
    // the mic + counter row sits below it (outside the border) once editing
    // begins. className (e.g. w-full max-w-md) goes on the outer wrapper so
    // both the bordered cell and the counter row share the same width
    // constraint and their right edges align.
    const charCount = (draftText ?? '').length;
    const isOverLimit =
      props.maxLength !== undefined && charCount > props.maxLength;
    const defaultCounterCls = MergeClassNames(
      'text-xs tabular-nums text-gray-muted',
      softLimit && isOverLimit && 'text-danger'
    );
    const counterText =
      props.maxLength !== undefined
        ? `${charCount}/${props.maxLength}${counterSuffix}`
        : undefined;

    // Inner bordered cell: same logic as rootClassName, className included so
    // the cell keeps its rest-state bg (e.g. bg-white on a grey card).
    const textCellRootClass = MergeClassNames(
      'relative flex items-center border border-transparent',
      !disabled && 'cursor-text',
      !isEditing && !disabled && 'hover:z-10 hover:border-success',
      isEditing && 'z-10 bg-white',
      isEditing && !error && 'border-success-dark',
      isEditing && error && 'border-danger',
      className
    );

    return (
      // Outer wrapper: className supplies width constraints (w-full, max-w-md…)
      // so the counter row below aligns with the cell. bg-transparent comes last
      // so twMerge strips any bg-* from className — the outer wrapper must be
      // transparent so the counter row shows the page's background, not white.
      <div
        className={MergeClassNames(
          'flex flex-col flex-1 min-w-0',
          className,
          'bg-transparent'
        )}
      >
        <div
          className={textCellRootClass}
          tabIndex={disabled || isEditing || skipTabStop ? -1 : 0}
          onFocus={() => !isEditing && enterEdit()}
          onClick={(event) => {
            event.stopPropagation();
            enterEdit();
          }}
        >
          {isEditing ? (
            <Input.TextArea
              ref={textInputRef}
              variant="borderless"
              className="editable-cell-text h-full w-full resize-none"
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder={props.placeholder}
              maxLength={softLimit ? undefined : props.maxLength}
              value={draftText ?? ''}
              onChange={(event) => setDraftText(event.target.value)}
              onBlur={commitText}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelEdit();
                }
              }}
            />
          ) : (
            <span className="line-clamp-2 block w-full px-2 py-1">
              {displayValue}
            </span>
          )}
        </div>
        {isEditing && (
          <div
            className={MergeClassNames(
              'mt-1 flex items-center gap-1',
              counterPosition === 'right' ? 'justify-end' : 'justify-start'
            )}
          >
            <VoiceMicButton
              isSupported={speechInput.isSupported}
              isRecording={speechInput.isRecording}
              lang={speechInput.lang}
              langOptions={speechInput.langOptions}
              onToggle={speechInput.toggle}
              onLangChange={speechInput.setLang}
              disabled={disabled}
            />
            {counterText !== undefined && (
              <span
                className={MergeClassNames(
                  counterClassName ?? defaultCounterCls
                )}
              >
                {counterText}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={rootClassName}
      {...{ [EDITABLE_NUMBER_CELL_ATTR]: '' }}
      aria-disabled={disabled || undefined}
      tabIndex={disabled || isEditing || skipTabStop ? -1 : 0}
      onFocus={() => !isEditing && enterEdit()}
      onClick={(event) => {
        event.stopPropagation();
        enterEdit();
      }}
    >
      {isEditing ? (
        // controlPaddingHorizontal matches the display span's px-2 (8px) so
        // text does not shift when switching between display and edit states.
        // rc-input-number reads controlPaddingHorizontal directly to generate
        // `padding: 0 Xpx` on the inner <input>; CSS !important cannot
        // override this because the value comes from a JS token, not a class.
        <ConfigProvider theme={{ token: { controlPaddingHorizontal: 8 } }}>
          <InputNumber
            ref={inputRef}
            variant="borderless"
            controls={false}
            keyboard={false}
            className="editable-cell-number h-full w-full"
            value={draftValue}
            onChange={(value) => setDraftValue(value)}
            onBlur={commitNumber}
            onPressEnter={commitNumber}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                cancelEdit();
                return;
              }
              const numberProps = props.type === 'number' ? props : null;
              const isPlainArrow =
                (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
                !event.shiftKey &&
                !event.altKey &&
                !event.ctrlKey &&
                !event.metaKey;
              if (numberProps?.onArrowNavigate && isPlainArrow) {
                // stopPropagation keeps the key from the step-reorder handler
                event.preventDefault();
                event.stopPropagation();
                // Invalid drafts stay put with their error rather than being stranded by the blur
                const validationError = getNumberDraftError();
                if (validationError) {
                  setError(validationError);
                  return;
                }
                numberProps.onArrowNavigate(
                  event.key === 'ArrowUp' ? 'up' : 'down',
                  event.currentTarget
                );
                return;
              }
              // Mirror mobile handleDecimalInput (/[^0-9.]/g): block any
              // printable character that is not a digit or decimal point.
              // key.length === 1 excludes control keys (Backspace, Delete,
              // Arrow, Tab, Enter). ctrlKey/metaKey guard preserves Ctrl+A/C/V/X.
              if (
                event.key.length === 1 &&
                !event.ctrlKey &&
                !event.metaKey &&
                /[^0-9.]/.test(event.key)
              ) {
                event.preventDefault();
              }
              // Block a second decimal point (mirrors mobile handleDecimalInput).
              // event.target is the inner <input> element; .value is its current
              // string before this keystroke is applied.
              if (
                event.key === '.' &&
                (event.target as HTMLInputElement).value.includes('.')
              ) {
                event.preventDefault();
              }
            }}
          />
        </ConfigProvider>
      ) : (
        <span className="block w-full truncate px-2 py-1 tabular-nums">
          {displayValue}
        </span>
      )}
      {isEditing && error && (
        <div className="absolute left-0 top-full z-10 mt-2 whitespace-nowrap rounded bg-danger-light px-2 py-1 text-xs font-semibold text-danger shadow">
          {error}
          <span className="absolute -top-1 left-2 h-2 w-2 rotate-45 bg-danger-light" />
        </div>
      )}
    </div>
  );
};

export default EditableCell;
